import { gql, useEnhancedMutation } from "@uplift-ltd/apollo";
import { SpaceBetween } from "@uplift-ltd/space-between";
import { makeUrl } from "@uplift-ltd/strings";
import React, { useCallback, useState } from "react";
import { CourseProgressEnum } from "__generated__/globalTypes";
import Alert from "components/common/Alert";
import Button from "components/common/Button";
import { COURSE_URL } from "constants/urls";
import useRouterNavigation from "hooks/useRouterNavigation";
import { useRouterQuery } from "hooks/useRouterQuery";
import CourseBody from "./CourseBody";
import CourseTitle from "./CourseTitle";
import {
  MarkLessonsCompleteMutation,
  MarkLessonsCompleteVariables,
} from "./__generated__/CourseContent";
import { CourseIntersection } from "./types";

const MARK_LESSONS_COMPLETE = gql`
  mutation MarkLessonsComplete($courseId: ID!, $lessonIds: [ID]) {
    markLessonsComplete(
      input: { clientMutationId: "MarkLessonsComplete", courseId: $courseId, lessonIds: $lessonIds }
    ) {
      success
      course {
        id
        courseProgress
      }
    }
  }
`;

interface CourseContentProps {
  course: CourseIntersection;
}

const CourseContent: React.FC<CourseContentProps> = ({ course }) => {
  const [markLessonsComplete] = useEnhancedMutation<
    MarkLessonsCompleteMutation,
    MarkLessonsCompleteVariables
  >(MARK_LESSONS_COMPLETE, {}, { auth: false });

  const { push } = useRouterNavigation();
  const { slug } = useRouterQuery<{ slug: string }>().routerQuery;
  const [error, setError] = useState("");

  const completeLessons = useCallback(async () => {
    const result = await markLessonsComplete({
      variables: {
        courseId: course.id,
        lessonIds: course.lessons.map(l => l.id),
      },
    });

    if (result.data?.markLessonsComplete?.success) {
      push(makeUrl(COURSE_URL, { slug, page: CourseProgressEnum.POSTTEST.toLocaleLowerCase() }));
    } else {
      setError("Oops. Something went wrong. Please try refreshing the page.");
    }
  }, [markLessonsComplete, course.id, course.lessons, push, slug]);

  return (
    <div id="lessons">
      <div>
        <SpaceBetween className="mt-14">
          {course.lessons.map(lesson => (
            <div key={lesson.id}>
              {lesson.title ? <CourseTitle>{lesson.title}</CourseTitle> : null}
              <CourseBody content={lesson.content || ""} />
            </div>
          ))}
        </SpaceBetween>
      </div>
      {error.length > 0 && (
        <Alert theme="danger" className="mt-4">
          {error}
        </Alert>
      )}
      {!course.isTimeExpiredForUser && (
        <div className="flex justify-center">
          <Button className="mt-14" theme="primary" onClick={completeLessons}>
            Proceed To The Posttest
          </Button>
        </div>
      )}
    </div>
  );
};

export default CourseContent;
